import React from "react";

import { cn } from "../../lib/utils";

/**
 * The production "Ask your assistant" pill — AI-suggestions border, lavender
 * icon badge, and reply-loop glyph. Single source of truth so the inline PDP
 * entry point and the floating FAB stay visually identical; the FAB just layers
 * fixed positioning / transitions on via `className`.
 *
 * Sets `--pdp-assistant-pill-hover-border` on itself so it works standalone
 * (the FAB has no parent container to inherit it from).
 */

const PILL_CLASS =
  "group focusable interactive type-fs-paragraph text-fs-text-primary border-(--slate-color-program-border-ai_suggestions) hover:border-(--pdp-assistant-pill-hover-border) bg-fs-surface-primary box-border inline-flex h-10 shrink-0 cursor-pointer items-center whitespace-nowrap rounded-full border py-0 pl-[9px] pr-(--slate-dimensions-4) transition-colors";

// One-time entrance "nudge" keyframes for the reply arrow (Web Animations API —
// no global @keyframes needed). Tuned to read as a small, friendly tick.
const NUDGE_FRAMES: Keyframe[] = [
  { transform: "translateX(0)" },
  { transform: "translateX(3px)" },
  { transform: "translateX(0)" },
];
const NUDGE_TIMING: KeyframeAnimationOptions = {
  duration: 450,
  easing: "ease-out",
};

export interface AskAssistantPillProps {
  onClick?: () => void;
  /** Extra classes merged onto the button (e.g. fixed positioning for the FAB). */
  className?: string;
  style?: React.CSSProperties;
  "aria-hidden"?: boolean;
  tabIndex?: number;
  dataTestId?: string;
  /** Play a one-time icon nudge the first time the pill scrolls into view. */
  nudgeOnEnter?: boolean;
}

/** Imperative handle for triggering the icon nudge from a parent (e.g. the FAB). */
export interface AskAssistantPillHandle {
  nudge: () => void;
}

const AskAssistantPill = React.forwardRef<
  AskAssistantPillHandle,
  AskAssistantPillProps
>(function AskAssistantPill(
  {
    onClick,
    className,
    style,
    "aria-hidden": ariaHidden,
    tabIndex,
    dataTestId,
    nudgeOnEnter = false,
  },
  ref
) {
  const iconRef = React.useRef<HTMLSpanElement>(null);
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const hasNudgedRef = React.useRef(false);

  React.useImperativeHandle(
    ref,
    () => ({
      nudge: () => iconRef.current?.animate(NUDGE_FRAMES, NUDGE_TIMING),
    }),
    []
  );

  React.useEffect(() => {
    if (!nudgeOnEnter) {
      return;
    }
    const node = buttonRef.current;
    if (!node) {
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasNudgedRef.current) {
          hasNudgedRef.current = true;
          iconRef.current?.animate(NUDGE_FRAMES, NUDGE_TIMING);
          observer.disconnect();
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [nudgeOnEnter]);

  return (
    <button
      ref={buttonRef}
      type="button"
      aria-label="Ask your assistant"
      aria-hidden={ariaHidden}
      tabIndex={tabIndex}
      data-test-id={dataTestId}
      onClick={onClick}
      className={cn(PILL_CLASS, className)}
      style={
        {
          "--pdp-assistant-pill-hover-border": "#D7A3FF",
          ...style,
        } as React.CSSProperties
      }
    >
      <span
        className="mr-(--slate-dimensions-2) flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[var(--pdp-assistant-icon-badge-bg)] transition-colors group-hover:bg-[var(--pdp-assistant-pill-hover-border)]"
        style={
          { "--pdp-assistant-icon-badge-bg": "#F6EAFF" } as React.CSSProperties
        }
        aria-hidden="true"
      >
        {/* Hover nudge (CSS) + one-time entrance nudge (Web Animations API).
            Prod uses .pdp-assistant-tude-nudge for the same effect. */}
        <span
          ref={iconRef}
          className="inline-flex transition-transform duration-200 ease-out group-hover:translate-x-0.5"
        >
          <AssistantReplyIcon />
        </span>
      </span>
      Ask your assistant
    </button>
  );
});

export default AskAssistantPill;

const AssistantReplyIcon: React.FC = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    className="size-4"
    aria-hidden="true"
  >
    <path d="M9.5 5.5L12.5 8.5L9.5 11.5" stroke="currentColor" strokeLinecap="round" />
    <path
      d="M12.5 8.5H5C4.16667 8.5 2.5 8 2.5 6C2.5 4 4.5 3.5 5 3.5"
      stroke="currentColor"
      strokeLinecap="round"
    />
  </svg>
);
